/**
 * Notification Toast Component
 * Shows real-time notifications for critical events
 */

import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, CheckCircle, Info, AlertCircle } from 'lucide-react';
import { logger } from '../utils/logger';

interface Notification {
  id: string;
  type: 'critical' | 'warning' | 'success' | 'info';
  title: string;
  message: string;
  timestamp: string;
}

interface NotificationToastProps {
  autoHide?: number; // milliseconds, 0 = no auto-hide
  autoRefresh?: boolean; // whether to poll for new events
}

const NotificationToast: React.FC<NotificationToastProps> = ({ autoHide = 15000, autoRefresh = true }) => { // 改為 15 秒
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [lastEventTime, setLastEventTime] = useState(Date.now());
  const [processedEventIds, setProcessedEventIds] = useState<Set<string>>(new Set());

  // Listen for Battery Alert events from App.tsx
  useEffect(() => {
    const handleBatteryAlert = (event: any) => {
      logger.log('🔋 NotificationToast: Received battery alert event:', event.detail);
      const alertData = event.detail;

      // Create notification from battery alert
      const notification: Notification = {
        id: alertData.id || Date.now().toString(),
        type: alertData.type,
        title: alertData.title,
        message: alertData.message,
        timestamp: alertData.timestamp
      };

      setNotifications(prev => [notification, ...prev].slice(0, 5));

      // Play sound for critical alerts
      if (alertData.type === 'critical') {
        playNotificationSound();
      }
    };

    window.addEventListener('new-alert', handleBatteryAlert);

    return () => {
      window.removeEventListener('new-alert', handleBatteryAlert);
    };
  }, []);

  useEffect(() => {
    // Only poll if autoRefresh is enabled
    if (!autoRefresh) return;

    // Poll for critical events
    const checkForCriticalEvents = async () => {
      // Skip event polling in development (API routes only work on Vercel)
      if (process.env.NODE_ENV === 'development') {
        return;
      }
      
      try {
        const response = await fetch(`/api/events/recent?since=${lastEventTime}`);
        if (response.ok) {
          const data = await response.json();
          
          // Process critical events
          if (data.critical && data.critical.length > 0) {
            const newEvents = data.critical.filter((event: any) => {
              const eventId = event.id || `${event.eventType}-${event.deviceSn}-${event.timestamp}`;
              return !processedEventIds.has(eventId);
            });
            
            if (newEvents.length > 0) {
              const newNotifications = newEvents.map((event: any) => {
                const eventId = event.id || `${event.eventType}-${event.deviceSn}-${event.timestamp}`;
                return {
                  id: eventId,
                  type: 'critical' as const,
                  title: getEventTitle(event.eventType),
                  message: event.message || getEventMessage(event.eventType, event.deviceSn),
                  timestamp: event.timestamp
                };
              });
              
              setNotifications(prev => [...newNotifications, ...prev].slice(0, 5)); // Keep max 5
              
              // Mark events as processed
              setProcessedEventIds(prev => {
                const newSet = new Set(prev);
                newEvents.forEach((event: any) => {
                  const eventId = event.id || `${event.eventType}-${event.deviceSn}-${event.timestamp}`;
                  newSet.add(eventId);
                });
                // Keep only last 100 processed IDs
                if (newSet.size > 100) {
                  const arr = Array.from(newSet);
                  return new Set(arr.slice(-100));
                }
                return newSet;
              });
              
              setLastEventTime(data.timestamp);
              
              // Play sound for critical events
              playNotificationSound();
            }
          }
        }
      } catch (error) {
        logger.error('Error checking for events:', error);
      }
    };

    const interval = setInterval(checkForCriticalEvents, 3000); // Check every 3 seconds

    return () => clearInterval(interval);
  }, [lastEventTime, autoRefresh]);

  const getEventTitle = (eventType: string): string => {
    const titles: Record<string, string> = {
      'door.open': '🚪 Security Alert',
      'power.overload': '⚡ Power Overload',
      'device.offline': '📡 Device Offline',
      'temperature.critical': '🌡️ Temperature Alert'
    };
    return titles[eventType] || '⚠️ System Alert';
  };

  const getEventMessage = (eventType: string, deviceSn?: string): string => {
    const messages: Record<string, string> = {
      'door.open': `Door/Window opened${deviceSn ? ` (${deviceSn})` : ''}`,
      'power.overload': `Power threshold exceeded${deviceSn ? ` on ${deviceSn}` : ''}`,
      'device.offline': `Device went offline${deviceSn ? ` (${deviceSn})` : ''}`,
      'temperature.critical': `Temperature out of range${deviceSn ? ` at ${deviceSn}` : ''}`
    };
    return messages[eventType] || `Event occurred${deviceSn ? ` on ${deviceSn}` : ''}`;
  };

  const playNotificationSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF');
      audio.volume = 0.3;
      audio.play().catch(() => {}); // Ignore if autoplay is blocked
    } catch (error) {
      // Ignore audio errors
    }
  };

  const dismissNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  useEffect(() => {
    if (autoHide > 0) {
      const timer = setInterval(() => {
        setNotifications(prev => {
          if (prev.length > 0) {
            const now = Date.now();
            return prev.filter(n => {
              const notifTime = new Date(n.timestamp).getTime();
              return now - notifTime < autoHide;
            });
          }
          return prev;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [autoHide]);

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'critical':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  // Format System log messages to be more human-readable
  const formatMessage = (notification: Notification): string => {
    const message = notification.message || '';
    const title = notification.title || '';
    
    // Check if this is a System log message (connection status)
    if (title === 'System' || title.toLowerCase().includes('system')) {
      // Check for disconnect indicators
      if (message.toLowerCase().includes('disconnect') || 
          message.toLowerCase().includes('timeout') ||
          message.toLowerCase().includes('mqtt_keep_alive_timeout')) {
        return '已離線 (Disconnected)';
      }
      // Check for connect indicators or generic system messages with serviceName
      if (message.includes('serviceName:') || 
          message.toLowerCase().includes('connect') ||
          message.includes('severity:')) {
        // If it contains disconnect-related info, it's disconnected
        if (message.toLowerCase().includes('disconnect')) {
          return '已離線 (Disconnected)';
        }
        // Otherwise assume connected
        return '已連線 (Connected)';
      }
    }
    
    return message;
  };

  if (notifications.length === 0) return null;

  return (
    <div className="fixed top-20 right-4 z-50 space-y-2 max-w-sm">
      {notifications.map(notification => (
        <div
          key={notification.id}
          className={`
            bg-slate-800/50/90 bg-[#0C2340]/90 rounded-lg shadow-lg p-4
            border-l-4 transform transition-all duration-300 animate-slide-in backdrop-blur-sm
            ${notification.type === 'critical' ? 'border-red-500' :
              notification.type === 'warning' ? 'border-yellow-500' :
              notification.type === 'success' ? 'border-green-500' : 'border-blue-500'}
          `}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              {getIcon(notification.type)}
              <div className="flex-1">
                <h4 className="font-semibold text-white">
                  {notification.title}
                </h4>
                <p className="text-sm text-slate-400 text-slate-300 mt-1">
                  {formatMessage(notification)}
                </p>
                <p className="text-xs text-slate-400 mt-2">
                  {new Date(notification.timestamp).toLocaleTimeString()}
                </p>
              </div>
            </div>
            <button
              onClick={() => dismissNotification(notification.id)}
              className="ml-4 text-slate-400 hover:text-slate-400 hover:text-slate-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default NotificationToast;